// Environment-driven API roots. `vite build` (production) loads .env.production,
// `vite build --mode staging` loads .env.staging, `vite dev` uses the local API.
// import.meta.env.DEV is true ONLY for the dev server — any built bundle
// (production OR staging) uses the VITE_* URLs of its mode, so a staging build
// can never silently fall back to the live API.
const isDev = import.meta.env.DEV;

const LOCAL_API_ROOT = import.meta.env.VITE_API_URL_LOCAL || "http://localhost:5001";
const DEPLOYED_API_ROOT = import.meta.env.VITE_API_URL || "https://api-cre.e360digitalpro.com";

const trimSlash = (url: string) => url.replace(/\/+$/, "");

// Each portal domain is paired with its own API, and the pairing is decided
// here rather than at build time. Two reasons:
//
//   1. One build serves every domain. Previously each domain needed its own
//      bundle, and shipping the wrong one silently pointed a portal at another
//      environment's API — which is exactly how it went wrong.
//   2. Every pair is same-site. The session lives in a cookie, and a cookie set
//      by a different registrable domain is a third-party cookie, which current
//      browsers drop even when it is correctly marked SameSite=None; Secure.
//      Login then returns 200 and every request after it returns 401.
//
// A host that is not listed falls back to the build's VITE_API_URL.
const API_BY_HOST: Record<string, string> = {
  "omniworkspace.app": "https://api.omniworkspace.app",
  "www.omniworkspace.app": "https://api.omniworkspace.app",
  "cre.e360digitalpro.com": "https://api-cre.e360digitalpro.com",
  "staging-cre.e360digitalpro.com": "https://api-staging-cre.e360digitalpro.com",
};

const apiForCurrentHost = (): string => {
  if (typeof window === "undefined") return "";
  const host = window.location.hostname.toLowerCase();
  if (API_BY_HOST[host]) return API_BY_HOST[host];
  // Tenant subdomains (e360, dymax, multi, and any added later) share the
  // central omni API, which keeps them same-site with it.
  if (host.endsWith(".omniworkspace.app")) return "https://api.omniworkspace.app";
  return "";
};

// Root of the API host (no /api suffix) — REST, sockets and downloads all
// derive from this single value so the environments can never drift apart.
export const API_ROOT = isDev
  ? trimSlash(LOCAL_API_ROOT)
  : trimSlash(apiForCurrentHost() || DEPLOYED_API_ROOT);

export const API_BASE_URL = `${API_ROOT}/api`;

export const BASE_URL = isDev ? "http://localhost:5001/" : import.meta.env.VITE_BASE_URL || API_ROOT;

export const DOWNLOAD_BASE_URL = isDev
  ? "http://localhost:5001/"
  : import.meta.env.VITE_DOWNLOAD_BASE_URL || API_ROOT;

export const ACCESS_TOKEN_NAME = "x-auth-token";

// "staging" | "production" | "" — drives the STAGING banner and title suffix.
export const APP_ENV = (import.meta.env.VITE_APP_ENV || (isDev ? "development" : "production")) as string;
export const IS_STAGING = APP_ENV === "staging";
